import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import { InputAdornment } from '@material-ui/core'

import { Dialog, snackbar, UploadButton } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { ERRORS, PRIZE } from 'consts'
import { resizeFile } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { TabPanel } from 'components/Dialog/TabPanel'
import CustomTextField from 'components/CustomTextField'
import PushNameTokenField from 'components/PushNameTokenField'

import { updateNotification } from '../api'
import { UpdateNotificationData, Notification, UpdateNotificationResponse } from '../types'
import { useStyles } from './styles'

interface ValidationData {
  toteatEnabled: boolean
  tucanCategory?: PrizeCategory
  toteatCategory?: PrizeCategory
}

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 50,
      message: 'No puede superar los 50 caracteres',
    },
  ],
  body: [
    {
      validate: (body: string) => body.length <= 192,
      message: 'No puede superar los 192 caracteres',
    },
  ],
  prizeTitle: [
    {
      validate: (title: string) => title.length <= 70,
      message: 'No puede superar los 70 caracteres',
    },
  ],
  prizeShortTitle: [
    {
      validate: (prizeShortTitle: string) => prizeShortTitle.length <= 40,
      message: 'No puede superar los 40 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => description.length <= 1024,
      message: 'No puede superar los 1024 caracteres',
    },
  ],
  expirationDays: [
    {
      validate: (value: string) =>
        ((value && Number(value) >= 1) || !value) && Number.isInteger(Number(value)),
      message: 'El vencimiento debe ser un número entero mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface UpdateNotificationDialogProps {
  notification: Notification
  handleClose: () => void
  onDone: (newNotification: Notification) => void
}

const UpdateNotificationDialog: React.FC<UpdateNotificationDialogProps> = ({
  notification,
  handleClose,
  onDone,
}) => {
  const [toteatBenefitId, setToteatBenefitId] = useState<string>(
    notification.prize.toteatBenefitId || '',
  )
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>(
    notification.prize.skus ? notification.prize.skus.map(s => s.sku) : [],
  )
  const [storesEnabled, setStoresEnabled] = useState(notification.prize.storesEnabled)
  const [tucanEnabled, setTucanEnabled] = useState(notification.prize.tucanEnabled)
  const [toteatEnabled, setToteatEnabled] = useState(notification.prize.toteatEnabled)
  const [tucanCategory, setTucanCategory] = useState<PrizeCategory | undefined>(
    notification.prize.tucanCategory,
  )
  const [toteatCategory, setToteatCategory] = useState<PrizeCategory | undefined>(
    notification.prize.toteatCategory,
  )
  const [title, setTitle] = useState(notification.title)
  const [body, setBody] = useState(notification.body)
  const [icon, setIcon] = useState<File | undefined>(undefined)
  const [iconPreview, setIconPreview] = useState(notification.prize.iconUrl)
  const [prizeTitle, setPrizeTitle] = useState(notification.prize.title)
  const [prizeShortTitle, setPrizeShortTitle] = useState(notification.prize.shortTitle)
  const [expirationDays, setExpirationDays] = useState<string>(
    notification.prize.expirationDays ? notification.prize.expirationDays.toString() : '30',
  )
  const [additionalText, setAdditionalText] = useState(
    notification.prize.additionalText ? notification.prize.additionalText : '',
  )
  const [activePrize, setActivePrize] = useState(notification.activePrize)
  const [active, setActive] = useState(notification.active)
  const [description, setDescription] = useState(notification.description)
  const [timeDays, setTimeDays] = useState<string>(
    notification.timeDays ? notification.timeDays.toString() : '',
  )
  const [visitsValue, setVisitsValue] = useState<string>(
    notification.visitsValue ? notification.visitsValue.toString() : '',
  )
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)

  const getRequiredFields = () => {
    const baseRequiredFields = ['title', 'body', 'description']
    const prizeRequiredFields = [
      'prizeTitle',
      'prizeShortTitle',
      'additionalText',
      'expirationDays',
    ]

    if (activePrize) {
      baseRequiredFields.push(...prizeRequiredFields)
    }

    if (toteatEnabled) {
      if (toteatCategory === PrizeCategory.General) {
        baseRequiredFields.push('skusList')
      } else if (toteatCategory === PrizeCategory.Checkout) {
        baseRequiredFields.push('toteatBenefitId')
      }
    }

    return baseRequiredFields
  }

  const dataCheck = {
    title,
    body,
    description,
    prizeTitle,
    prizeShortTitle,
    additionalText,
    expirationDays,
    activePrize,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    tucanCategory,
    toteatCategory,
  }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(
    dataCheck,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors || hasPartnerErrors

  const [updateNotificationApi, isLoading] = useApiCall<
    UpdateNotificationData,
    UpdateNotificationResponse
  >(updateNotification)

  const handleSubmit = async () => {
    if (activePrize && !iconPreview) {
      snackbar.show('Para activar el premio es necesario cargar el icono')
      return
    }

    const data = {
      title,
      body,
      icon,
      active,
      activePrize,
      description,
      timeDays: timeDays ? parseInt(timeDays, 10) : undefined,
      visitsValue: visitsValue ? parseInt(visitsValue, 10) : undefined,
      prize: {
        title: prizeTitle,
        shortTitle: prizeShortTitle,
        additionalText,
        expirationDays,
        tucanCategory,
        toteatCategory,
        storesEnabled,
        tucanEnabled,
        toteatEnabled,
        skus: skusList,
        toteatBenefitId,
      },
    }

    try {
      const { notification: updatedNotification } = await updateNotificationApi({
        id: notification.id,
        payload: data,
      })
      onDone(updatedNotification)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERRORS.GENERIC_ERROR_MESSAGE
      snackbar.show(errorMessage)
    }
  }

  const handleIconChange = async (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    const image = await resizeFile(file, 192, 192)
    setIcon(image)
  }

  const handleChangeActivePrize: React.ChangeEventHandler<HTMLInputElement> = () => {
    setActivePrize(!activePrize)
  }

  const handleChangeActive: React.ChangeEventHandler<HTMLInputElement> = () => {
    setActive(!active)
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  const classes = useStyles()
  const tabs = ['Descripción', 'Beneficio', 'Asignación']

  return (
    <Dialog
      title="Editar notificación"
      isOpen
      onCancel={handleClose}
      showTabsActions
      okButtonText="Editar notificación"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      isLoading={isLoading}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
      style={{
        minWidth: 600,
        minHeight: 640,
        borderRadius: 15,
      }}
    >
      <TabPanel value={tabIndex} index={0} id="description">
        <Grid container spacing={3}>
          <Grid item xs={11}>
            <CustomFormControlLabel
              control={<CustomCheckbox onChange={handleChangeActive} checked={active} />}
              label="Activar notificación"
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              fullWidth
              label="Descripción Push Automática"
              required
              maxLength={1024}
              onChange={e => setDescription(e.target.value)}
              value={description}
              error={errors.description.hasError}
              helperText={errors.description.message}
            />
          </Grid>
          <Grid item xs={12}>
            <PushNameTokenField
              fullWidth
              label="Título"
              required
              maxLength={50}
              onChange={e => setTitle(e.target.value)}
              value={title}
              error={errors.title.hasError}
              helperText={errors.title.message}
            />
          </Grid>
          <Grid item xs={12}>
            <PushNameTokenField
              fullWidth
              label="Mensaje"
              required
              multiline
              maxLength={192}
              onChange={e => setBody(e.target.value)}
              value={body}
              error={errors.body.hasError}
              helperText={errors.body.message}
            />
          </Grid>
          {notification.code !== 'WELCOME_PRIZE' &&
            notification.code !== 'REGISTRATION_ANNIVERSARY' &&
            notification.code !== 'REGISTRATION_MONTH' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tiempo de ejecución (días)"
                  type="number"
                  value={timeDays}
                  onChange={e => setTimeDays(e.target.value)}
                  helperText="Número de días para ejecutar la automatización (opcional, se usará el valor por defecto si no se especifica)"
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                />
              </Grid>
            )}
          {notification.code === 'VISIT_MILESTONE' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cantidad de visitas requeridas"
                type="number"
                value={visitsValue}
                onChange={e => setVisitsValue(e.target.value)}
                helperText="Número de visitas necesarias para activar esta notificación (opcional, por defecto 50)"
                InputProps={{
                  inputProps: { min: 1 },
                }}
              />
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={1} id="benefit">
        <Grid container spacing={3}>
          <Grid item xs={11}>
            <CustomFormControlLabel
              control={<CustomCheckbox onChange={handleChangeActivePrize} checked={activePrize} />}
              label="Activar premio"
            />
          </Grid>
          {activePrize && (
            <>
              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  label="Título"
                  required
                  maxLength={70}
                  onChange={e => setPrizeTitle(e.target.value)}
                  value={prizeTitle}
                  error={errors.prizeTitle.hasError}
                  helperText={errors.prizeTitle.message}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  label="Descripción"
                  required
                  maxLength={40}
                  onChange={e => setPrizeShortTitle(e.target.value)}
                  value={prizeShortTitle}
                  error={errors.prizeShortTitle.hasError}
                  helperText={errors.prizeShortTitle.message}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  label="Descripción Adicional"
                  required
                  maxLength={100}
                  onChange={e => setAdditionalText(e.target.value)}
                  value={additionalText}
                  error={errors.additionalText.hasError}
                  helperText={errors.additionalText.message}
                />
              </Grid>
              {notification.prize.rewardType === PRIZE.REWARD_TYPE.AUTOMATIC && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    value={expirationDays}
                    type="number"
                    fullWidth
                    onChange={e => setExpirationDays(e.target.value)}
                    disabled={isLoading}
                    label="Vencimiento"
                    required
                    InputProps={{
                      inputProps: { min: 1 },
                      endAdornment: <InputAdornment position="start">días</InputAdornment>,
                    }}
                    error={errors.expirationDays.hasError}
                    helperText={errors.expirationDays.message}
                  />
                </Grid>
              )}
              <Grid container xs={12} className={classes.iconUploadContainer}>
                <Grid item xs={6} className={classes.iconUploadButton}>
                  <UploadButton
                    id="manualReward-icon-upload-button"
                    accept=".jpg, .jpeg, .png"
                    label="Subir ícono premio *"
                    onChange={handleIconChange}
                    maxSizeInMB={2}
                  />
                </Grid>
                <Grid item xs={6}>
                  <div className={classes.iconUploadPreviewContainer}>
                    <img src={iconPreview} alt="" className={classes.iconPreview} />
                  </div>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={2} id="assignment">
        <Grid container spacing={3}>
          {activePrize && (
            <PartnerIntegration
              storesEnabled={storesEnabled}
              setStoresEnabled={setStoresEnabled}
              tucanEnabled={tucanEnabled}
              setTucanEnabled={setTucanEnabled}
              toteatEnabled={toteatEnabled}
              setToteatEnabled={setToteatEnabled}
              tucanCategory={tucanCategory}
              setTucanCategory={setTucanCategory}
              toteatCategory={toteatCategory}
              setToteatCategory={setToteatCategory}
              currentSku={currentSku}
              setCurrentSku={setCurrentSku}
              skusList={skusList}
              setSkusList={setSkusList}
              toteatBenefitId={toteatBenefitId}
              setToteatBenefitId={setToteatBenefitId}
              errors={errors}
              isLoading={isLoading}
              onValidationChange={handlePartnerValidationChange}
            />
          )}
        </Grid>
      </TabPanel>
    </Dialog>
  )
}

export { UpdateNotificationDialog }
